import React, { useEffect, useState } from "react";
import axiosAuth from "../Axios/Interceptors";
import AppContext from "../Context/Context";
type PackingJobProps = {
  QuoteID?: number | string;
  IsReadOnly?: boolean;
};

type PackingItem = {
  MNVPackingJobItemID: number;
  Room: string;
  PackingTimeInHrs: number;
  VanSpaceInPercentage: number;
  Notes: string;
  [k: string]: any;
};

const defaultNewItem = (): PackingItem => ({
  MNVPackingJobItemID: 0,
  Room: "",
  PackingTimeInHrs: 0,
  VanSpaceInPercentage: 0,
  Notes: "",
});

const formatMoney = (v: number | null | undefined) =>
  v == null
    ? "£0.00"
    : `£${v?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const PackingJob: React.FC<PackingJobProps> = ({
  QuoteID,
  IsReadOnly = false,
}) => {
  // packing items
  const [packingJobItemData, setPackingJobItemData] = useState<PackingItem[]>(
    [],
  );
  const [addNewObject, setAddNewObject] =
    useState<PackingItem>(defaultNewItem());
  const [addNew, setAddNew] = useState(false);

  // materials
  const [TemplateTypeID, setTemplateTypeID] = useState<number>(0);
  const [MaterialsTemplate, setMaterialsTemplate] = useState(false);
  const [MaterialsTemplateTypes, setMaterialsTemplateTypes] = useState<any[]>(
    [],
  );
  const [MaterialsTemplateData, setMaterialsTemplateData] = useState<any[]>([]);

  // ui
  const [isLoading, setIsLoading] = useState(false);
  const [showModalSendPaymentLink, setShowModalSendPaymentLink] =
    useState(false);
  const [showModalMaterialsList, setShowModalMaterialsList] = useState(false);

  // initial load
  useEffect(() => {
    MNVMaterialsPriceTemplateTypeGetCompleteGridDataCustom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // watch QuoteID changes (ngOnChanges)
  useEffect(() => {
    setTemplateTypeID(0);
    MNVPackingJobItemGetDataSetByMNVQuoteIDCustom();

    if (TemplateTypeID == 0) {
      setMaterialsTemplateData([]);
      MNVMaterialsPriceTemplateGetDataByTemplateType(0);
    }
  }, [QuoteID]);

  useEffect(() => {
    setMaterialsTemplateData([]);
    MNVMaterialsPriceTemplateGetDataByTemplateType(0);
  }, [TemplateTypeID]);

  // --------------------------
  // API calls (converted)
  // --------------------------
  async function MNVPackingJobItemGetDataSetByMNVQuoteIDCustom() {
    if (!QuoteID || QuoteID === "" || Number(QuoteID) <= 0) {
      setPackingJobItemData([]);
      setMaterialsTemplateData([]);
      return;
    }

    try {
      setIsLoading(true);
      const data: any = { MNVQuoteID: QuoteID };
      const res = await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVPackingJobItem/MNVPackingJobItemGetDataSetByMNVQuoteIDCustom",
        data,
      );
      // original Angular expects result[0]
      setPackingJobItemData((res.data && res.data[0]) || []);
    } catch (err) {
      console.error(
        "MNVPackingJobItemGetDataSetByMNVQuoteIDCustom error:",
        err,
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function MNVPackingJobItemSave() {
    try {
      setIsLoading(true);
      const data: any = {
        MNVQuoteID: QuoteID,
        MNVPackingJobItemID: addNewObject.MNVPackingJobItemID,
        Room: addNewObject.Room,
        PackingTimeInHrs: addNewObject.PackingTimeInHrs,
        VanSpaceInPercentage: addNewObject.VanSpaceInPercentage,
        Notes: addNewObject.Notes,
      };
      await axiosAuth.post(
        AppContext.APIURL + "api/MNVPackingJobItem/MNVPackingJobItemSave",
        data,
      );

      // close modal and reset
      setShowModalSendPaymentLink(false);
      setAddNew(false);
      setAddNewObject(defaultNewItem());
      // refresh list
      await MNVPackingJobItemGetDataSetByMNVQuoteIDCustom();
    } catch (err) {
      console.error("MNVPackingJobItemSave error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  function MNVPackingJobItemAddNewRecord() {
    setAddNewObject(defaultNewItem());
    setAddNew(true);
    setShowModalSendPaymentLink(true);
  }

  function MNVPackingJobItemGetRecordForEdit(id: number) {
    const found = packingJobItemData.find((x) => x.MNVPackingJobItemID === id);
    if (found) {
      setAddNewObject({ ...found });
      setShowModalSendPaymentLink(true);
    }
  }

  async function MNVPackingJobItemDeleteRecord(MNVPackingJobItemID: number) {
    try {
      const data = { MNVPackingJobItemID };
      setIsLoading(true);
      await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVPackingJobItem/MNVPackingJobItemDeleteRecord",
        data,
      );
      await MNVPackingJobItemGetDataSetByMNVQuoteIDCustom();
    } catch (err) {
      console.error("MNVPackingJobItemDeleteRecord error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  async function MNVMaterialsPriceTemplateTypeGetCompleteGridDataCustom() {
    try {
      setIsLoading(true);
      const res = await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVQuote/MNVMaterialsPriceTemplateTypeGetCompleteGridDataCustom",
        {},
      );
      const l_arrResponse = (res.data && res.data[0]) || res.data || [];
      setMaterialsTemplateTypes(l_arrResponse);
    } catch (err) {
      console.error(
        "MNVMaterialsPriceTemplateTypeGetCompleteGridDataCustom error:",
        err,
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function MNVMaterialsPriceTemplateGetDataByTemplateType(isAdd: number) {
    try {
      setIsLoading(true);
      const data: any = {
        templatetypeid: TemplateTypeID,
        mnvquoteid: QuoteID,
      };
      const res = await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVQuote/MNVMaterialsPriceTemplateGetDataByTemplateType",
        data,
      );
      const l_arrResponse = res.data || [];
      setMaterialsTemplateData(l_arrResponse);
      let newTemplateTypeId = 0;
      if (
        l_arrResponse.length > 0 &&
        Array.isArray(l_arrResponse[0]) &&
        l_arrResponse[0].length > 0
      ) {
        newTemplateTypeId = l_arrResponse[0][0]["TemplateTypeID"] || 0;
      }
      setTemplateTypeID(newTemplateTypeId);

      if (isAdd) {
        // reload from server (Angular did a second call)
        await MNVMaterialsPriceTemplateGetDataByTemplateType(0);
      }
    } catch (err) {
      console.error(
        "MNVMaterialsPriceTemplateGetDataByTemplateType error:",
        err,
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function RelMNVMaterialsPriceTemplateAndMNVQuoteSaveCustom(
    NumberOfUnit: any,
    RelMNVMaterialsPriceTemplateAndMNVQuoteID: any,
    MNVMaterialsPriceTemplateID: any,
  ) {
    try {
      setIsLoading(true);
      const data: any = {
        relmnvmaterialspricetemplateandmnvquoteid:
          RelMNVMaterialsPriceTemplateAndMNVQuoteID,
        numberofunit: NumberOfUnit,
        mnvmaterialspricetemplateid: MNVMaterialsPriceTemplateID,
        mnvquoteid: QuoteID,
      };
      await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVQuote/RelMNVMaterialsPriceTemplateAndMNVQuoteSaveCustom",
        data,
      );
      // refresh template data
      await MNVMaterialsPriceTemplateGetDataByTemplateType(0);
    } catch (err) {
      console.error(
        "RelMNVMaterialsPriceTemplateAndMNVQuoteSaveCustom error:",
        err,
      );
    } finally {
      setIsLoading(false);
    }
  }

  function OpenPopupSendPaymentLink() {
    setShowModalSendPaymentLink(true);
  }

  function OpenPopupMaterialsList() {
    setShowModalMaterialsList(true);
  }

  function DeleteDataConfirmation() {
    // Swal.fire({
    //   title: "Are you sure?",
    //   text: "Are you sure you want to delete data? You will not be able to revert it.",
    //   icon: "warning",
    //   showCancelButton: true,
    //   confirmButtonColor: "#3085d6",
    //   cancelButtonColor: "#d33",
    //   confirmButtonText: "Yes, do it!",
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     setTemplateTypeID(-1);
    //     MNVMaterialsPriceTemplateGetDataByTemplateType(0);
    //   }
    // });

    setTemplateTypeID(-1);
  }

  // --------------------------
  // Render
  // --------------------------

  async function updateBoxEsReturnData(p_IsBoxesReturned: any) {
    try {
      setIsLoading(true);
      const data: any = {
        MNVQuoteID: QuoteID,
        IsBoxesReturned: p_IsBoxesReturned,
      };
      const res = await axiosAuth.post(
        AppContext.APIURL + "api/MNVQuoteAdmin/UpdateBoxEsReturnData",
        data,
      );
    } catch (err) {
      console.error("updateBoxEsReturnData error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {/* Main content: only show if QuoteID valid */}
      {QuoteID && QuoteID !== "" && Number(QuoteID) > 0 && (
        <>
          {/* Buttons */}
          {!addNew && !IsReadOnly && (
            <div
              className="flex-daj btn-start flex-gap10 mt-20"
              style={{ marginBottom: 12 }}
            >
              <a
                className="flex-daj btn-primary"
                onClick={() => {
                  MNVPackingJobItemAddNewRecord();
                }}
              >
                Add Room
              </a>

              {!IsReadOnly && MaterialsTemplateData.length === 0 && (
                <a
                  className="flex-daj btn-primary"
                  onClick={() => OpenPopupMaterialsList()}
                >
                  Add Materials
                </a>
              )}
            </div>
          )}

          {/* Packing items + van guide */}
          {/* {packingJobItemData && packingJobItemData.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "5fr 1fr",
                alignItems: "flex-start",
                gap: 20,
              }}
            >
              <span />
              <span style={{ fontWeight: "bold", fontSize: 14 }}>
                Van Guide
              </span>
            </div>
          )} */}

          {packingJobItemData && packingJobItemData.length > 0 && (
            <div className="grid-d fr-51 flex-gap20 mt-10">
              <div className="flow-auto mt-25 auto-height">
                <table className="reuse-table fixed-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Pack Time</th>
                      <th>Van % Space</th>
                      <th>Notes</th>
                      {!IsReadOnly && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {packingJobItemData.map((Item, index) => (
                      <tr
                        key={Item.MNVPackingJobItemID}
                        className={
                          Item.MNVPackingJobItemID === 1000000
                            ? "bold-font"
                            : ""
                        }
                      >
                        <td className="text-left">{Item.Room}</td>
                        <td className="text-left">
                          {(Number(Item.PackingTimeInHrs) || 0).toFixed(2)}{" "}
                          {Item.MNVPackingJobItemID === 1000000 && (
                            <span>hrs</span>
                          )}
                        </td>
                        <td className="text-left">
                          {(Number(Item.VanSpaceInPercentage) || 0).toFixed(2)}{" "}
                          {Item.MNVPackingJobItemID === 1000000 && (
                            <span>van(s)</span>
                          )}
                        </td>
                        <td className="text-left">{Item.Notes}</td>
                        {!IsReadOnly && (
                          <td>
                            {Item.MNVPackingJobItemID !== 1000000 && (
                              <>
                                <div className="flex-daj flex-gap10">
                                  <a
                                    className="flex-d"
                                    onClick={() => {
                                      MNVPackingJobItemGetRecordForEdit(
                                        Item.MNVPackingJobItemID,
                                      );
                                    }}
                                  >
                                    <img
                                      src="/images/edit.svg"
                                      width="18px"
                                      height="18px"
                                      alt="Edit"
                                    />
                                  </a>

                                  <a
                                    className="flex-d"
                                    onClick={() =>
                                      MNVPackingJobItemDeleteRecord(
                                        Item.MNVPackingJobItemID,
                                      )
                                    }
                                  >
                                    <img
                                      src="/images/delete.png"
                                      width="18px"
                                      height="18px"
                                      alt="Edit"
                                    />
                                  </a>
                                </div>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Van guide table */}
              <div className="flow-auto auto-height">
                <span className="font-14 text-left label-color bold-font flex-d">
                  Van Guide
                </span>
                <table className="reuse-table fixed-table">
                  <tbody>
                    {/* Hardcoded cells (as original) */}
                    {[0, 1, 2, 3, 4].map((r) => (
                      <tr key={r}>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          10%
                        </td>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          10%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Materials */}
          {MaterialsTemplateData.length > 0 && (
            <div className="grid-d max-wid-1000">
              {!IsReadOnly && (
                <div className="flex-daj flex-gap10 flex-jse">
                  <a
                    className="flex-d"
                    onClick={() => OpenPopupMaterialsList()}
                  >
                    <img
                      src="/images/edit.svg"
                      width="18px"
                      height="18px"
                      alt="Edit"
                    />
                  </a>

                  <a
                    className="flex-d"
                    onClick={() => DeleteDataConfirmation()}
                  >
                    <img
                      src="/images/delete.png"
                      width="18px"
                      height="18px"
                      alt="Edit"
                    />
                  </a>
                </div>
              )}

              <div className="grid-d fr-11 max-wid-1000 flex-gap20 mt-20 table-clean">
                <div className="flow-auto auto-height check-bold2">
                  <table className="reuse-table fixed-table">
                    <tbody>
                      {MaterialsTemplateData[0] &&
                        MaterialsTemplateData[0].map((item: any, i: number) => {
                          if (
                            [1, 3, 4, 5].includes(item.MNVMaterialsListPriceID)
                          ) {
                            return (
                              <tr key={i}>
                                <td className="text-left">
                                  {item.ListPriceName}
                                </td>

                                <td className="pack-input-td">
                                  <input
                                    className="text-right"
                                    type="text"
                                    disabled={IsReadOnly}
                                    value={item.NumberOfUnit ?? ""}
                                    onChange={(e) => {
                                      // update local MaterialsTemplateData copy
                                      const copy = [...MaterialsTemplateData];
                                      copy[0][i].NumberOfUnit = e.target.value;
                                      setMaterialsTemplateData(copy);
                                    }}
                                    onBlur={() =>
                                      RelMNVMaterialsPriceTemplateAndMNVQuoteSaveCustom(
                                        MaterialsTemplateData[0][i]
                                          .NumberOfUnit,
                                        MaterialsTemplateData[0][i]
                                          .RelMNVMaterialsPriceTemplateAndMNVQuoteID,
                                        MaterialsTemplateData[0][i]
                                          .MNVMaterialsPriceTemplateID,
                                      )
                                    }
                                  />
                                </td>

                                <td>
                                  {item.DiffInUnits === 0 ? (
                                    ""
                                  ) : item.DiffInUnits > 0 ? (
                                    <label className="c-green-label">
                                      {" "}
                                      +{item.DiffInUnits}
                                    </label>
                                  ) : (
                                    <label className="c-red">
                                      {item.DiffInUnits}
                                    </label>
                                  )}
                                </td>

                                {!IsReadOnly && (
                                  <td className="text-right">
                                    {formatMoney(item.RentingAmount)}
                                  </td>
                                )}
                                {!IsReadOnly && (
                                  <td className="text-right">
                                    {formatMoney(item.BuyingAmount)}
                                  </td>
                                )}
                              </tr>
                            );
                          }
                          return null;
                        })}
                      {/* Summary block (MaterialsTemplateData[1]) */}
                      {MaterialsTemplateData[1] &&
                        !IsReadOnly &&
                        MaterialsTemplateData[1][0] && (
                          <>
                            <tr>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td className="text-center bold-font">Exc VAT</td>
                              <td className="text-center bold-font">Inc VAT</td>
                            </tr>

                            <tr>
                              <td className="bold-font text-left">Total</td>

                              <td></td>
                              <td className="bold-font text-right add-bor">
                                Renting
                              </td>
                              <td className="text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0].RentingAmount,
                                )}
                              </td>
                              <td className="bold-font text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0]
                                    .RentingAmountWithVAT,
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td></td>

                              <td></td>
                              <td className="bold-font text-right add-bor">
                                Buying
                              </td>
                              <td className="text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0].BuyingAmount,
                                )}
                              </td>
                              <td className="bold-font text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0]
                                    .BuyingAmountWithVAT,
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td className="c-red bold-font text-left">
                                Difference
                              </td>
                              <td></td>
                              <td></td>
                              <td className="c-red text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0]
                                    .DifferenceInAmount,
                                )}
                              </td>
                              <td className="bold-font c-red text-right add-bor">
                                {formatMoney(
                                  MaterialsTemplateData[1][0]
                                    .DifferenceInAmountWithVAT,
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td className="bold-font text-left" colSpan={2}>
                                Number Of Boxes Used
                              </td>
                              <td></td>

                              <td className=" text-left bold-font">
                                <span>
                                  {MaterialsTemplateData[2][0]?.NumberOfBoxes}
                                </span>
                              </td>
                              <td className="c-red text-right"></td>
                            </tr>
                            <tr>
                              <td className="bold-font text-left" colSpan={2}>
                                Mover Notes For Materials
                              </td>
                              <td></td>
                              <td className=" text-left " colSpan={2}>
                                <span>
                                  {
                                    MaterialsTemplateData[2][0]
                                      ?.MoverNotesForMaterials
                                  }
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="bold-font text-left">Refund</td>
                              <td></td>
                              <td></td>
                              <td className=" text-left ">
                                <input
                                  className="input-refund"
                                  type="checkbox"
                                  defaultChecked={
                                    MaterialsTemplateData[2][0]?.IsBoxesReturned
                                  }
                                  onChange={(e) => {
                                    updateBoxEsReturnData(e.target.checked);
                                  }}
                                />
                              </td>
                              <td className="c-red text-right"></td>
                            </tr>
                          </>
                        )}
                    </tbody>
                  </table>
                </div>

                {/* Right column for materials not in left group */}
                <div className="flow-auto auto-height check-bold">
                  <table className="reuse-table fixed-table">
                    <tbody>
                      {MaterialsTemplateData[0] &&
                        MaterialsTemplateData[0].map((item: any, i: number) => {
                          if (
                            ![1, 3, 4, 5].includes(item.MNVMaterialsListPriceID)
                          ) {
                            return (
                              <tr key={`r-${i}`}>
                                <td className="text-left">
                                  {item.ListPriceName}
                                </td>

                                <td className="pack-input-td">
                                  <input
                                    className="text-right"
                                    type="text"
                                    disabled={IsReadOnly}
                                    value={item.NumberOfUnit ?? ""}
                                    onChange={(e) => {
                                      const copy = [...MaterialsTemplateData];
                                      copy[0][i].NumberOfUnit = e.target.value;
                                      setMaterialsTemplateData(copy);
                                    }}
                                    onBlur={() =>
                                      RelMNVMaterialsPriceTemplateAndMNVQuoteSaveCustom(
                                        MaterialsTemplateData[0][i]
                                          .NumberOfUnit,
                                        MaterialsTemplateData[0][i]
                                          .RelMNVMaterialsPriceTemplateAndMNVQuoteID,
                                        MaterialsTemplateData[0][i]
                                          .MNVMaterialsPriceTemplateID,
                                      )
                                    }
                                  />
                                </td>

                                <td>
                                  {item.DiffInUnits === 0 ? (
                                    ""
                                  ) : item.DiffInUnits > 0 ? (
                                    <label className="c-green-label">
                                      {" "}
                                      +{item.DiffInUnits}
                                    </label>
                                  ) : (
                                    <label className="c-red">
                                      {item.DiffInUnits}
                                    </label>
                                  )}
                                </td>

                                {!IsReadOnly && (
                                  <td className="text-right">
                                    {formatMoney(item.RentingAmount)}
                                  </td>
                                )}
                              </tr>
                            );
                          }
                          return null;
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Modals: Send Payment Link (Add/Edit Packing Item) */}
          {showModalSendPaymentLink && (
            <div className="popup-help popup-help-index">
              <div className="help-popup-display">
                <div className="help-inner">
                  <div className="help-head flex-da justify-btw">
                    <h3 className="bold-font flex-d m-all0">
                      {addNewObject.MNVPackingJobItemID === 0
                        ? "Add New"
                        : "Edit"}
                    </h3>
                    <a
                      className="close-help"
                      onClick={() => {
                        setShowModalSendPaymentLink(false);
                        setAddNew(false);
                      }}
                    >
                      <img
                        src="/images/close.svg"
                        width="15px"
                        height="15px"
                        alt="cross"
                      />
                    </a>
                  </div>

                  <div className="modal-body">
                    <div className="grid-d fr-1 gap-20 mt-20">
                      <div className="field-input grid-d flex-gap3">
                        <div className="font-12 text-left label-color">
                          Room
                        </div>
                        <input
                          type="text"
                          className="input-field w-100"
                          value={addNewObject.Room}
                          onChange={(e) =>
                            setAddNewObject({
                              ...addNewObject,
                              Room: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="field-input grid-d flex-gap3">
                        <div className="font-12 text-left label-color">
                          Pack Time
                        </div>
                        <input
                          type="text"
                          className="input-field w-100"
                          value={addNewObject.PackingTimeInHrs}
                          onChange={(e) =>
                            setAddNewObject({
                              ...addNewObject,
                              PackingTimeInHrs: e.target.value as any,
                            })
                          }
                        />
                      </div>

                      <div className="field-input grid-d flex-gap3">
                        <div className="font-12 text-left label-color">
                          Van % Space
                        </div>
                        <input
                          type="text"
                          className="input-field w-100"
                          value={addNewObject.VanSpaceInPercentage}
                          onChange={(e) =>
                            setAddNewObject({
                              ...addNewObject,
                              VanSpaceInPercentage: e.target.value as any,
                            })
                          }
                        />
                      </div>

                      <div className="field-input grid-d flex-gap3">
                        <div className="font-12 text-left label-color">
                          Notes
                        </div>
                        <input
                          type="text"
                          className="input-field w-100"
                          value={addNewObject.Notes}
                          onChange={(e) =>
                            setAddNewObject({
                              ...addNewObject,
                              Notes: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="flex-daj btn-start mt-10 flex-gap10">
                        <a
                          className="flex-daj btn-primary"
                          onClick={() => MNVPackingJobItemSave()}
                        >
                          Save
                        </a>
                        <a
                          className="flex-daj btn-primary"
                          onClick={() => {
                            setShowModalSendPaymentLink(false);
                            setAddNew(false);
                          }}
                        >
                          Cancel
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Materials List */}
          {showModalMaterialsList && (
            <div className="popup-help popup-help-index">
              <div className="help-popup-display">
                <div className="help-inner">
                  <div className="help-head flex-da justify-btw">
                    <h3 className="bold-font flex-d m-all0">Template Type</h3>
                    <a
                      className="close-help"
                      onClick={() => setShowModalMaterialsList(false)}
                    >
                      <img
                        src="/images/close.svg"
                        width="15px"
                        height="15px"
                        alt="cross"
                      />
                    </a>
                  </div>

                  <div className="modal-body">
                    <div className="field-input grid-d flex-gap3 mt-20">
                      <div className="font-12 text-left label-color">
                        Template Type
                      </div>
                      <select
                        value={TemplateTypeID}
                        onChange={(e) =>
                          setTemplateTypeID(Number(e.target.value))
                        }
                        className="input-field w-100"
                      >
                        <option value={0}>Please Select</option>
                        {MaterialsTemplateTypes.map(
                          (item: any, idx: number) => (
                            <option
                              key={idx}
                              value={item.MNVMaterialsPriceTemplateTypeID}
                            >
                              {item.Name}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div className="flex-daj btn-start mt-10 flex-gap10">
                      <a
                        className="flex-daj btn-primary"
                        onClick={() => {
                          MNVMaterialsPriceTemplateGetDataByTemplateType(1);
                          setShowModalMaterialsList(false);
                        }}
                      >
                        Save
                      </a>
                      <a
                        className="flex-daj btn-primary"
                        onClick={() => setShowModalMaterialsList(false)}
                      >
                        Cancel
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PackingJob;
